import { useMutation } from '@apollo/client';
import { faPencil, faArrowRotateRight, faTrashCan, faExternalLink, faPlug } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { url } from '../../../core/utils/link.ts';
import { format } from 'date-fns';
import classnames from '../../../core/utils/classnames.tsx';
import {
    CHECK_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION_CONNECTION,
    DELETE_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION,
    GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS,
    TRIGGER_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION_MASTERDATA_UPDATE,
} from '../../queries.ts';
import { CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_EDIT_PATH } from '../../constants.ts';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';

const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const LoadingIcon = withIcon(faSpinnerThird);
const ExternalLinkIcon = withIcon(faExternalLink);
const ConnectionIcon = withIcon(faPlug);

export const DynamicsFinanceOperationsRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION, {
        refetchQueries: [GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('dynamicsFinanceOperations.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('dynamicsFinanceOperations.deleteError'), status: 'error' });
        }
    };

    const [updateMasterDataIntegration] = useMutation(
        TRIGGER_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION_MASTERDATA_UPDATE
    );
    const handleUpdateMasterDataIntegration = async () => {
        try {
            await updateMasterDataIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('dynamicsFinanceOperations.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('dynamicsFinanceOperations.updateMasterDataError'), status: 'error' });
        }
    };

    const [checkConnection, { loading: checkingConnection }] = useMutation(
        CHECK_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION_CONNECTION
    );
    const [connectionState, setConnectionState] = useState<boolean | null>(null);
    const handleCheckConnection = async () => {
        try {
            const res = await checkConnection({ variables: { id: integration.id } });
            const { connected, message } = res.data.checkDynamicsFinanceOperationsIntegrationConnection;
            setConnectionState(connected);
            publishToast({
                description: connected
                    ? t('dynamicsFinanceOperations.checkConnectionSuccess')
                    : message || t('dynamicsFinanceOperations.checkConnectionFailed'),
                status: connected ? 'success' : 'error',
            });
        } catch (e) {
            console.error(e);
            setConnectionState(false);
            publishToast({ description: t('dynamicsFinanceOperations.checkConnectionError'), status: 'error' });
        }
    };

    const orderChannels = integration ? [...integration.orderChannels] : [];
    const rfqChannels = integration ? [...integration.rfqChannels] : [];

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name}</Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {orderChannels.map((channel) => (
                            <span
                                key={channel.id}
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {rfqChannels.map((channel) => (
                            <span
                                key={channel.id}
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    {integration.masterDataUpdateActive ? (
                        <span className="flex gap-2 items-center animate-pulse">
                            <span className="text-content-success-strong">
                                <LoadingIcon spin />
                            </span>
                            <span>
                                {`${t('dynamicsFinanceOperations.masterDataUpdateInProgress')} ${(integration.fetchProgress * 100).toFixed(2)}%`}
                            </span>
                        </span>
                    ) : integration.masterDataUpdateError ? (
                        <div className="flex flex-col gap-1">
                            <span className="text-content-danger-strong">
                                {t('dynamicsFinanceOperations.masterDataUpdateError')}
                            </span>
                            <span className="text-content-medium text-sm whitespace-pre-line">
                                {integration.masterDataUpdateError}
                            </span>
                        </div>
                    ) : integration.masterDataUpdatedAt ? (
                        <span>
                            {t('dynamicsFinanceOperations.lastMasterDataUpdate', {
                                updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
                            })}
                        </span>
                    ) : (
                        <span className="text-content-medium">—</span>
                    )}
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <RowControlIconButton
                            onClick={handleCheckConnection}
                            disabled={checkingConnection}
                            title={t('dynamicsFinanceOperations.checkConnection')}
                            className={classnames(
                                connectionState === true && 'text-content-success-strong',
                                connectionState === false && 'text-content-danger-strong'
                            )}
                        >
                            {checkingConnection ? <LoadingIcon spin /> : <ConnectionIcon />}
                        </RowControlIconButton>

                        <RowControlIconButton
                            onClick={handleUpdateMasterDataIntegration}
                            title={t('dynamicsFinanceOperations.triggerUpdate')}
                        >
                            <RefreshIcon />
                        </RowControlIconButton>

                        {integration.environment && (
                            <RowControlIconButton asChild>
                                <Link
                                    to={integration.environment}
                                    target="_blank"
                                    className="!text-content-strong hover:!text-content-strong"
                                >
                                    <ExternalLinkIcon />
                                </Link>
                            </RowControlIconButton>
                        )}

                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_EDIT_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={handleDeleteIntegration}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};
